import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Pencil, Briefcase, X, Check } from "lucide-react";
import { toast } from "sonner";

export default function CompaniesSection({ agency, onToggleRaggruppa }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCompany, setNewCompany] = useState({ nome: "", codice: "", email: "", email_cc: "" });
  const [editingId, setEditingId] = useState(null);
  const [editingDraft, setEditingDraft] = useState({ nome: "", codice: "", attivo: true, email: "", email_cc: "" });

  const enabled = !!agency?.plurimandatari_abilitato;

  const load = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setItems(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [enabled]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newCompany.nome.trim()) { toast.error("Nome obbligatorio"); return; }
    try {
      const { data } = await api.post("/companies", {
        nome: newCompany.nome.trim(),
        codice: newCompany.codice.trim(),
        attivo: true,
        email: newCompany.email.trim() || null,
        email_cc: newCompany.email_cc.trim() || null,
      });
      setItems((cur) => [...cur, data].sort((a, b) => a.nome.localeCompare(b.nome)));
      setNewCompany({ nome: "", codice: "", email: "", email_cc: "" });
      toast.success("Compagnia aggiunta");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditingDraft({
      nome: c.nome,
      codice: c.codice || "",
      attivo: !!c.attivo,
      email: c.email || "",
      email_cc: c.email_cc || "",
    });
  };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async (id) => {
    if (!editingDraft.nome.trim()) { toast.error("Nome obbligatorio"); return; }
    try {
      const { data } = await api.put(`/companies/${id}`, {
        nome: editingDraft.nome.trim(),
        codice: editingDraft.codice.trim(),
        attivo: editingDraft.attivo,
        email: editingDraft.email.trim(),
        email_cc: editingDraft.email_cc.trim(),
      });
      setItems((cur) => cur.map((c) => (c.id === id ? data : c)));
      setEditingId(null);
      toast.success("Compagnia aggiornata");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id, nome) => {
    if (!window.confirm(`Eliminare la compagnia "${nome}"?`)) return;
    try {
      const { data } = await api.delete(`/companies/${id}`);
      if (data.soft) {
        toast.info(`"${nome}" è in uso su ${data.in_use} polizze: disattivata invece di eliminata.`);
        setItems((cur) => cur.map((c) => (c.id === id ? { ...c, attivo: false } : c)));
      } else {
        setItems((cur) => cur.filter((c) => c.id !== id));
        toast.success("Compagnia eliminata");
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!enabled) {
    return (
      <Card className="p-6 border-slate-200" data-testid="companies-section-disabled">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
            <Briefcase className="w-4 h-4 text-slate-700" />
          </div>
          <h2 className="font-heading text-lg font-semibold">Compagnie (plurimandatari)</h2>
        </div>
        <p className="text-sm text-slate-600">
          Opzione <strong>Plurimandatari</strong> non attiva. Attivala da <strong>Abbonamento → Servizi inclusi</strong> per gestire più compagnie sullo stesso foglio cassa. Inclusa nei piani <strong>Solo</strong>, <strong>Pro</strong> e <strong>Unlimited</strong>.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-6 border-slate-200" data-testid="companies-section">
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
            <Briefcase className="w-4 h-4 text-slate-700" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-semibold">Compagnie (plurimandatari)</h2>
            <div className="text-xs text-slate-500">Add-on attivo: una colonna &quot;Compagnia&quot; comparirà nel foglio cassa.</div>
          </div>
        </div>
      </div>

      {/* Fogli cassa: unico o diviso per compagnia */}
      <div className="flex items-start justify-between gap-4 mb-5 p-3 rounded-lg bg-slate-50 border border-slate-100">
        <div className="flex-1">
          <Label className="text-slate-700">Fogli cassa separati per compagnia</Label>
          <div className="text-xs text-slate-500 mt-1">
            <strong>Spento</strong> → foglio cassa unico con tutte le compagnie. <br />
            <strong>Acceso</strong> → il foglio mostra i totali divisi per compagnia (con un PDF per ciascuna) e l&apos;email di chiusura contiene il PDF completo più un PDF per ogni compagnia.
          </div>
        </div>
        <Switch
          data-testid="agency-fogli-per-compagnia"
          checked={agency.raggruppa_compagnie === undefined ? false : !agency.raggruppa_compagnie}
          onCheckedChange={(v) => onToggleRaggruppa(!v)}
        />
      </div>

      {/* Add form */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-2">
        <div className="md:col-span-5">
          <Label className="text-xs">Nome compagnia</Label>
          <Input
            data-testid="company-new-nome"
            value={newCompany.nome}
            onChange={(e) => setNewCompany({ ...newCompany, nome: e.target.value })}
            placeholder="Es. Allianz, Generali, UnipolSai…"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Codice</Label>
          <Input
            data-testid="company-new-codice"
            value={newCompany.codice}
            onChange={(e) => setNewCompany({ ...newCompany, codice: e.target.value })}
            placeholder="ALZ"
          />
        </div>
        <div className="md:col-span-5">
          <Label className="text-xs">Email invio incassi (opzionale)</Label>
          <Input
            type="email"
            data-testid="company-new-email"
            value={newCompany.email}
            onChange={(e) => setNewCompany({ ...newCompany, email: e.target.value })}
            placeholder="incassi@compagnia.it"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-4">
        <div className="md:col-span-7 md:col-start-1">
          <Label className="text-xs">Email in copia CC (opzionale)</Label>
          <Input
            type="email"
            data-testid="company-new-email-cc"
            value={newCompany.email_cc}
            onChange={(e) => setNewCompany({ ...newCompany, email_cc: e.target.value })}
            placeholder="commerciale@compagnia.it"
          />
        </div>
        <div className="md:col-span-3 flex items-end">
          <div className="text-[11px] text-slate-500 leading-snug">
            L&apos;email invia un PDF snello con gli incassi giornalieri della compagnia.
          </div>
        </div>
        <div className="md:col-span-2 flex items-end">
          <Button onClick={create} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="company-add">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi
          </Button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-sm text-slate-500">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-4">Nessuna compagnia ancora inserita.</div>
      ) : (
        <div className="overflow-x-auto border border-slate-100 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-500">Nome</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-500">Codice</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-500">Email incassi</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-500">CC</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-500">Attiva</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-500 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50" data-testid={`company-row-${c.id}`}>
                  {editingId === c.id ? (
                    <>
                      <td className="px-3 py-2"><Input value={editingDraft.nome} onChange={(e) => setEditingDraft({ ...editingDraft, nome: e.target.value })} /></td>
                      <td className="px-3 py-2"><Input value={editingDraft.codice} onChange={(e) => setEditingDraft({ ...editingDraft, codice: e.target.value })} /></td>
                      <td className="px-3 py-2">
                        <Input
                          type="email"
                          value={editingDraft.email}
                          onChange={(e) => setEditingDraft({ ...editingDraft, email: e.target.value })}
                          placeholder="incassi@compagnia.it"
                          data-testid={`company-edit-email-${c.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="email"
                          value={editingDraft.email_cc}
                          onChange={(e) => setEditingDraft({ ...editingDraft, email_cc: e.target.value })}
                          placeholder="CC"
                          data-testid={`company-edit-email-cc-${c.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Switch checked={editingDraft.attivo} onCheckedChange={(v) => setEditingDraft({ ...editingDraft, attivo: v })} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => saveEdit(c.id)} className="mr-1" data-testid={`company-save-${c.id}`}><Check className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={cancelEdit}><X className="w-4 h-4" /></Button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2 font-medium text-slate-900">{c.nome}</td>
                      <td className="px-3 py-2 text-slate-600">{c.codice || "—"}</td>
                      <td className="px-3 py-2 text-slate-600">
                        {c.email ? (
                          <span className="text-xs">{c.email}</span>
                        ) : (
                          <span className="text-xs text-slate-400 italic">non configurata</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{c.email_cc || "—"}</td>
                      <td className="px-3 py-2">
                        {c.attivo
                          ? <span className="text-emerald-700 text-xs font-medium">Attiva</span>
                          : <span className="text-slate-400 text-xs">Disattivata</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => startEdit(c)} className="mr-1" data-testid={`company-edit-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => remove(c.id, c.nome)} data-testid={`company-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
